import { useState, useCallback } from 'react';
import { AccessCheckResult, BodyUpdateDocumentPermissionsApiDocumentsDocumentIdPermissionsPut } from '@/api/ApiClient';
import { apiClient } from '@/config/apiConfig';

type PermissionUpdate = Partial<BodyUpdateDocumentPermissionsApiDocumentsDocumentIdPermissionsPut>;

export const useDocumentPermissions = (documentId: string, onPermissionsUpdated?: () => void) => {
  const [editMode, setEditMode] = useState(false);
  const [accessInfo, setAccessInfo] = useState<AccessCheckResult | null>(null);

  // Loading and error states (replaces useAsyncOperation)
  const [isLoadingAccess, setIsLoadingAccess] = useState(false);
  const [accessError, setAccessError] = useState<Error | null>(null);
  const [isUpdatingPermissions, setIsUpdatingPermissions] = useState(false);
  const [updateError, setUpdateError] = useState<Error | null>(null);

  const [permissions, setPermissions] = useState<PermissionUpdate>({
    visibility: 'private',
    purpose: 'general',
    shared_with_users: '',
    shared_with_groups: '',
    shared_with_roles: '',
    retention_days: undefined
  });

  const loadAccessInfo = useCallback(async (): Promise<AccessCheckResult | undefined> => {
    setIsLoadingAccess(true);
    setAccessError(null);
    try {
      const response = await apiClient.checkDocumentAccessApiDocumentsDocumentIdAccessGet(documentId);
      const result = response.data;
      setAccessInfo(result);
      return result;
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Failed to load access info');
      setAccessError(err);
      return undefined;
    } finally {
      setIsLoadingAccess(false);
    }
  }, [documentId]);

  const handleUpdatePermissions = async (): Promise<void> => {
    if (!accessInfo?.can_modify_permissions) {
      setUpdateError(new Error('You do not have permission to modify this document'));
      return;
    }

    setIsUpdatingPermissions(true);
    setUpdateError(null);
    try {
      await apiClient.updateDocumentPermissionsApiDocumentsDocumentIdPermissionsPut(
        documentId,
        permissions as BodyUpdateDocumentPermissionsApiDocumentsDocumentIdPermissionsPut
      );

      await loadAccessInfo();
      setEditMode(false);

      if (onPermissionsUpdated) {
        onPermissionsUpdated();
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Failed to update permissions');
      setUpdateError(err);
    } finally {
      setIsUpdatingPermissions(false);
    }
  };

  const handleFieldChange = (field: string, value: unknown): void => {
    setPermissions(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return {
    editMode,
    setEditMode,
    permissions,
    accessInfo,
    isLoadingAccess,
    accessError,
    isUpdatingPermissions,
    updateError,
    loadAccessInfo,
    handleUpdatePermissions,
    handleFieldChange
  };
};